import { seoLinks, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { KnowledgeHubNote } from "../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../components/site/NewcomerNote";

const title = "Bo i Cancelada och Estepona med familj – områdesguide";
const description =
  "Områdesguide till Cancelada, Estepona och New Golden Mile: boende, pendling, vardagsservice, fritid och årstider för svenska familjer på Costa del Sol.";

export const Route = createFileRoute("/bo-i-cancelada-och-estepona")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/bo-i-cancelada-och-estepona"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Bo i Cancelada och Estepona"
        intro="Vår skola ligger i Cancelada, mellan Estepona och Marbella. Det är också här många av våra familjer bor. Här är en ärlig områdesguide till vardagen: boende, avstånd, service, fritid och hur årstiderna faktiskt känns."
      />

      <NewcomerNote />

      <Prose>
        <h2>Var ligger Cancelada?</h2>
        <p>
          Cancelada är en mindre ort längs kusten på den så kallade New Golden Mile, i Esteponas
          kommun och strax väster om Marbella. Området har vuxit snabbt de senaste åren, men har
          fortfarande kvar en bykänsla med torg, bagerier, restauranger och små butiker där man
          känner igen folk. För en familj innebär det något ganska ovanligt på kusten: närhet till
          allt, utan att bo mitt i turistströmmen.
        </p>
        <p>
          Från Cancelada tar man sig snabbt både till Estepona centrum i väster och till Marbella,
          Puerto Banús och San Pedro i öster. Malaga flygplats ligger vanligtvis kring en timme bort
          med bil, och Gibraltar något närmare åt andra hållet – praktiskt när släkt och vänner
          kommer på besök.
        </p>

        <h2>Boende</h2>
        <p>
          Utbudet spänner från lägenheter i urbanisationer med gemensam pool till radhus och villor
          längre upp mot bergen. Många svenska familjer hyr första året och köper – om de köper –
          först när de vet vilket område som passar vardagen. Det är ett klokt upplägg: avståndet
          mellan skola, jobb och träning märks mycket mer i verkligheten än på en karta.
        </p>
        <p>
          Tänk igenom om ni vill bo inom gångavstånd till skolan, nära stranden eller högre upp med
          utsikt. Kolla också praktiska saker som parkering, luftkonditionering, uppvärmning på
          vintern och hur urbanisationens avgifter fungerar. Hyresmarknaden rör sig snabbt inför
          skolstart i augusti och september.
        </p>

        <h2>Vardagsservice</h2>
        <p>
          Stora matbutiker, apotek, vårdcentral, tandläkare, gym och padelanläggningar finns inom
          några minuter. Estepona centrum har ett större utbud, marknader och en välskött gamla stan
          med blomsterprydda gränder. Marbella och San Pedro erbjuder mer av shopping, restauranger
          och specialistvård. I praktiken behöver de flesta familjer bil, även om mycket går att
          lösa till fots i Cancelada.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="sv" />
      </div>

      <Prose>

        <h2>Fritid och året runt</h2>
        <p>
          Klimatet är den uppenbara orsaken till att många flyttar hit: mycket sol, milda vintrar och
          en utomhussäsong som sträcker sig över nästan hela året. Fotboll, padel, tennis, simning,
          ridning, golf och havet finns runt hörnet, och för barn betyder det att aktiviteter blir en
          naturlig del av veckan snarare än något man kör långt för.
        </p>
        <p>
          Samtidigt ska man veta att sommaren är het och att juli och augusti är turisttäta, medan
          november till februari kan vara regnigare och svalare på kvällarna än man föreställer sig.
          Bostäder byggda för värme kan kännas kalla i januari. Det är inget problem – men det är
          bra att veta innan man packar garderoben.
        </p>

        <h2>Att komma in i gemenskapen</h2>
        <p>
          Området är internationellt, och det finns en stor svensktalande krets. Det gör
          inflyttningen enklare, men det kan också bli en bubbla om man inte anstränger sig lite.
          Vår erfarenhet är att familjer som snabbt hittar en aktivitet – en klubb, ett lag, en
          padelgrupp – landar fortare. Skolan är ofta den första naturliga mötesplatsen; läs mer om
          hur vi arbetar med{" "}
          <Link className="text-primary link-quiet" to="/gemenskap">
            gemenskap och mottagande
          </Link>
          .
        </p>

        <h2>Checklista inför flytten</h2>
        <ul>
          <li>Besök området i lågsäsong, inte bara på semestern.</li>
          <li>Testa körsträckan mellan tänkt boende och skolan på en vardagsmorgon.</li>
          <li>Hyr innan ni köper, minst ett läsår.</li>
          <li>Räkna med bil, och kontrollera parkering vid boendet.</li>
          <li>Boka skolplats i god tid – platserna är begränsade i vissa årskurser.</li>
          <li>Planera myndighetsärendena parallellt, inte efteråt.</li>
        </ul>

        <p>
          Familjer som är antagna och har betalat inskrivningsavgiften får en genomgång av just sin
          situation inför flytten om de önskar – den gäller vid minst ett barn på heltid, minst en
          termin, och kryssas i i ansökan. Läs mer om{" "}
          <Link className="text-primary link-quiet" to="/flyttgenomgang">
            genomgången inför flytten
          </Link>{" "}
          eller{" "}
          <Link className="text-primary link-quiet" to="/kontakt">
            kontakta oss
          </Link>
          .
        </p>
      </Prose>

      <KnowledgeHubNote resource="vardagen-i-spanien" />

      <GuideLinks current="/bo-i-cancelada-och-estepona" />
      <CtaBand />
      <div className="h-20" />
    </>
  );
}
